import React from 'react';
import { TreeNode } from '../../../../../../types/global';
import { KeyboardArrowUp, KeyboardArrowDown } from '@mui/icons-material';
import {
  TableRow,
  TableCell,
  IconButton,
  Checkbox,
  Collapse,
  Table,
  TableBody,
  Box,
} from '@mui/material';
import { observer } from 'mobx-react';
import {
  IMAGES_DATASETS_BUCKET,
  SEGMENTED_DATASETS_BUCKET,
  CLASSIFIED_DATASETS_BUCKET,
  DELIVERY_REPORTS_BUCKET,
  MOSAICS_DATASETS_BUCKET,
} from '../../../../../../constants/base';
import { notificationStore } from '../../../../../../store/Notification';
import DataStore from '../../../../../../store';
import BaseButton from '../../../../../../components/BaseButton';
import { downloadButonAvailable } from '../../../../../../helpers/groups';
import { checkDownloadStatusAndDownload } from '../../../../../../helpers/request';
import { PIPELINE_MAX_SELECTED_WELLS } from '../../../../../../helpers/constants';

const SearchCollapseRow = observer(
  ({
    node,
    depth,
    onSelect,
    selected,
    fullPath,
    handleOpenModal,
    orgData,
    stats,
  }: {
    node: TreeNode;
    depth: number;
    onSelect: (path: any, node: any) => void;
    selected: string[];
    fullPath: string[];
    handleOpenModal: any;
    orgData: any;
    stats?: {
      selected: number;
      total: number;
    };
  }) => {
    const [open, setOpen] = React.useState(false);
    const hasChildren =
      node.children && node.children.length > 0 && node.children[0].name !== '';
    const path = [...fullPath, node.name];

    const joinedPath = path.join('/');
    const isSelected = selected.includes(joinedPath);

    const handleSelect = () => {
      const currentBranch = path[1];
      let existingBranches = [];
      let isSameBranch = true;

      if (path[0] === IMAGES_DATASETS_BUCKET) {
        existingBranches = selected.map((p) => p.split('/')[1]).filter(Boolean);

        isSameBranch = existingBranches.every((b) => b === currentBranch);
      }

      const isSelecting = !isSelected;

      if (isSelecting && existingBranches.length > 0 && !isSameBranch) {
        notificationStore.showMessage(
          'In images-datasets you can only select items from a single scan.',
          'error'
        );
        return;
      }

      onSelect(path, node);
    };

    const getPathsByCategory = (category: string) => {
      if (selected.length === 0) {
        return [];
      }
      const relevant = selected.filter((path) => path.startsWith(category));

      const findNodeByPath = (
        tree: TreeNode,
        parts: string[]
      ): TreeNode | null => {
        let current = tree;

        for (const part of parts) {
          const next = current.children?.find((child) => child.name === part);
          if (!next) return null;
          current = next;
        }

        return current;
      };

      const collectLeafPaths = (
        node: TreeNode,
        basePath: string[]
      ): string[] => {
        if (
          !node.children ||
          node.children.length === 0 ||
          node.children.every((c) => c.name === '')
        ) {
          return [basePath.join('/')];
        }

        const paths: string[] = [];
        for (const child of node.children) {
          if (child.name) {
            paths.push(...collectLeafPaths(child, [...basePath, child.name]));
          }
        }
        return paths;
      };

      const result: string[] = [];

      for (const path of relevant) {
        const parts = path.split('/');
        const node = findNodeByPath(orgData, parts);

        const hasExplicitlySelectedChildren = relevant.some(
          (other) => other !== path && other.startsWith(`${path}/`)
        );

        if (!node) continue;

        if (hasExplicitlySelectedChildren) {
          continue;
        }

        const leafPaths = collectLeafPaths(node, parts);
        result.push(...leafPaths);
      }

      return result.map((p) => `s3://${p}`);
    };

    const handlePipelineLaunch = (category: string) => {
      const inputPaths = getPathsByCategory(category);
      if (inputPaths.length > 0) {
        if (inputPaths.length > PIPELINE_MAX_SELECTED_WELLS) {
          notificationStore.showMessage(
            `You can only launch pipelines with up to ${PIPELINE_MAX_SELECTED_WELLS} input paths.`,
            'error'
          );
          return;
        }
        DataStore.setPipelinePreview(false);
        DataStore.setCredits(0);
        handleOpenModal({
          type: category,
          pipelines: {
            inputPaths,
          },
        });
      } else {
        notificationStore.showMessage('No scan folder was selected.', 'error');
      }
    };

    const handleDownload = async (category: string) => {
      const inputPaths = getPathsByCategory(category);
      if (inputPaths.length === 0) {
        notificationStore.showMessage(
          'No folder was selected for download.',
          'error'
        );
        return;
      }

      try {
        const res = await DataStore.searchDownloadRequest(inputPaths);

        if (res.messageType === 'error') {
          notificationStore.showMessage(res.message, 'error');
          return;
        }

        const jobId = res.response?.download_id;
        if (!jobId) {
          notificationStore.showMessage('Unexpected response format.', 'error');
          return;
        }
        notificationStore.showMessage('Preparing download...', 'info');
        await checkDownloadStatusAndDownload(jobId);

        notificationStore.showMessage('Download completed.', 'success');
      } catch (err: any) {
        notificationStore.showMessage(
          err.message || 'Download failed.',
          'error'
        );
      }
    };

    const renderActionButtons = (category: string) => {
      const isProcessing = DataStore.isProcessing;

      const showBothButtons = [
        SEGMENTED_DATASETS_BUCKET,
        CLASSIFIED_DATASETS_BUCKET,
      ];

      const buttonGroup = (buttons: React.ReactNode) => (
        <Box display='flex' justifyContent='flex-end'>
          {buttons}
        </Box>
      );

      const containsAny = (source: string, targets: string[]) =>
        targets.some((target) => source.includes(target));

      if (containsAny(category, showBothButtons)) {
        return buttonGroup(
          <>
            {downloadButonAvailable(category) ? (
              <BaseButton
                text='Download'
                variant='outlined'
                size='small'
                color='primary'
                isDisabled={isProcessing}
                spinner={isProcessing}
                onClick={() => handleDownload(category)}
              />
            ) : null}

            <BaseButton
              text='Pipeline'
              variant='outlined'
              size='small'
              color='primary'
              isDisabled={isProcessing}
              spinner={isProcessing}
              onClick={() => handlePipelineLaunch(category)}
            />
          </>
        );
      }

      if (
        category.includes(MOSAICS_DATASETS_BUCKET) ||
        category.includes(DELIVERY_REPORTS_BUCKET)
      ) {
        return buttonGroup(
          <BaseButton
            text='Download'
            variant='outlined'
            size='small'
            color='primary'
            isDisabled={isProcessing}
            spinner={isProcessing}
            onClick={() => handleDownload(category)}
          />
        );
      } else if (category.includes(IMAGES_DATASETS_BUCKET)) {
        return buttonGroup(
          <BaseButton
            text='Pipeline'
            variant='outlined'
            size='small'
            color='primary'
            isDisabled={isProcessing}
            spinner={isProcessing}
            onClick={() => handlePipelineLaunch(category)}
          />
        );
      } else {
        return null;
      }
    };

    return (
      <>
        <TableRow
          className='output-collapse-row'
          sx={{
            height: '28px',
            '&:last-of-type td': {
              borderBottom: 'none',
            },
          }}
        >
          <TableCell sx={{ padding: '2px 4px', width: '28px' }}>
            {hasChildren && (
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <IconButton
                  size='small'
                  onClick={() => setOpen(!open)}
                  sx={{ padding: '2px' }}
                >
                  {open ? (
                    <KeyboardArrowUp fontSize='small' />
                  ) : (
                    <KeyboardArrowDown fontSize='small' />
                  )}
                </IconButton>
                {depth === 0 && (
                  <Checkbox
                    size='small'
                    checked={isSelected}
                    onChange={handleSelect}
                    sx={{ padding: '2px' }}
                  />
                )}
              </div>
            )}
          </TableCell>
          <TableCell sx={{ padding: '2px 4px' }}>
            <Box
              display='flex'
              alignItems='center'
              gap={0.5}
              sx={{ pl: `${depth * 1}rem`, width: '100%' }}
            >
              {depth > 0 && (
                <Checkbox
                  size='small'
                  checked={isSelected}
                  onChange={handleSelect}
                  sx={{ padding: '2px' }}
                />
              )}
              <div
                onClick={() => setOpen(!open)}
                style={{ width: '100%', cursor: 'pointer' }}
              >
                {node.name || '(empty)'}
                {stats && (
                  <span style={{ color: '#888', fontSize: '0.85em' }}>
                    {' '}
                    ({stats.selected}/{stats.total})
                  </span>
                )}
              </div>
            </Box>
          </TableCell>
          {depth === 0 && (
            <TableCell
              align='right'
              width={'220px'}
              sx={{ padding: '2px 4px', whiteSpace: 'nowrap' }}
            >
              {renderActionButtons(node.name)}
            </TableCell>
          )}
        </TableRow>
        {hasChildren && (
          <TableRow
            sx={{
              '&:last-of-type td': {
                borderBottom: 'none',
              },
            }}
          >
            <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={3}>
              <Collapse in={open} timeout='auto' unmountOnExit>
                <Table size='small' aria-label='children'>
                  <TableBody>
                    {node &&
                      node.children?.map((child, index) => (
                        <SearchCollapseRow
                          key={index}
                          orgData={orgData}
                          node={child}
                          depth={depth + 1}
                          onSelect={(subPath, node) =>
                            onSelect([...subPath], node)
                          }
                          selected={selected}
                          fullPath={path}
                          handleOpenModal={handleOpenModal}
                        />
                      ))}
                  </TableBody>
                </Table>
              </Collapse>
            </TableCell>
          </TableRow>
        )}
      </>
    );
  }
);

export default SearchCollapseRow;
